import i18next, { type i18n as I18nInstance, type Resource } from "i18next";
import { I18nextProvider, Trans, initReactI18next, useTranslation as useI18nTranslation } from "react-i18next";

export { I18nextProvider as I18nProvider, Trans };
import enCommon from "./locales/en/common";
import arCommon from "./locales/ar/common";
export { corpusLocatorText, legalBasisText } from "./legal";
export { geographyText, marketText } from "./geography";
export { authorityText } from "./authorities";
export { dataSourceText } from "./sources";
export { jobTitleText } from "./titles";
export { sectorText } from "./sectors";
export { PERIODS_LOADED, applyProcedurePeriods, loadProcedurePeriods, type ProcedurePeriods } from "./periods";
import { PERIODS_LOADED, PERIOD_PREFIX } from "./periods";
export { digitsNear, formatAed, formatAedCompact, formatAedNear, formatIndexNear, formatNumber, formatNumberNear, formatPercent, formatPercentNear, hidesMark, type Marks } from "./numbers";
import { formatNumber } from "./numbers";

export type Locale = "en" | "ar";
export const SUPPORTED_LOCALES: readonly Locale[] = ["en", "ar"] as const;
export const STORAGE_KEY = "ncp.locale";

export const isRtl = (locale: string): boolean => locale.startsWith("ar");
export const dirFor = (locale: string): "rtl" | "ltr" => (isRtl(locale) ? "rtl" : "ltr");

export interface CreateI18nOptions {
  /** App-specific namespaces merged on top of the shared `common` namespace. */
  resources?: Partial<Record<Locale, Record<string, object>>>;
  defaultLocale?: Locale;
}

function detectLocale(fallback: Locale): Locale {
  if (typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "ar") return stored;
  const nav = window.navigator.language?.toLowerCase() ?? "";
  return nav.startsWith("ar") ? "ar" : fallback;
}

export function applyDocumentLocale(locale: Locale): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
  document.documentElement.dir = dirFor(locale);
}

const ARABIC_PLURAL_FORMS = ["zero", "one", "two", "few", "many", "other"] as const;

/**
 * i18next looks up the exact Arabic plural form (`_zero`, `_two`, `_few` for 3–10, `_many` for 11–99) and then falls back to English, so a
 * counted message written once as "label: {{count}}" with only `_one` / `_other` would show in English for most counts. The forms a catalogue
 * leaves out take its `_other` text; forms it spells out are kept.
 */
function completeArabicPlurals(node: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(node)) out[k] = v && typeof v === "object" ? completeArabicPlurals(v as Record<string, unknown>) : v;
  for (const k of Object.keys(node)) {
    const base = k.endsWith("_other") ? k.slice(0, -"_other".length) : null;
    if (base === null) continue;
    for (const form of ARABIC_PLURAL_FORMS) if (out[`${base}_${form}`] === undefined) out[`${base}_${form}`] = node[k];
  }
  return out;
}

export function createI18n(options: CreateI18nOptions = {}): I18nInstance {
  return buildI18n(options, arCommon);
}

/**
 * As `createI18n`, without the shared catalogue's Arabic half: the app passes it in `resources.ar.common` when it boots in Arabic, or adds
 * it later with `addArabicCommon` (it loads on its own from `@ncp/i18n/ar-common`, so an English first screen does not carry it — the
 * ECONIC bundle budget SH-22).
 */
export function createI18nLean(options: CreateI18nOptions = {}): I18nInstance {
  return buildI18n(options, null);
}

/** Adds a bundle the app loaded after start-up; Arabic gets the plural forms `createI18n` would have completed. */
export function addLoadedBundle(instance: I18nInstance, locale: Locale, ns: string, bundle: object): void {
  instance.addResourceBundle(locale, ns, locale === "ar" ? completeArabicPlurals(bundle as Record<string, unknown>) : bundle, true, true);
}

function buildI18n(options: CreateI18nOptions, arabicCommon: object | null): I18nInstance {
  const defaultLocale = options.defaultLocale ?? "en";
  const initial = detectLocale(defaultLocale);
  const resources: Resource = {
    en: { common: enCommon, ...(options.resources?.en ?? {}) },
    ar: completeArabicPlurals({ ...(arabicCommon ? { common: arabicCommon } : {}), ...(options.resources?.ar ?? {}) }) as Resource[string],
  };

  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    resources,
    lng: initial,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    defaultNS: "common",
    ns: Object.keys(resources.en ?? {}),
    // The procedures' periods are read as `{{period.ec.initialReview}}` from the published definitions (applyProcedurePeriods); a period not yet
    // loaded reads as a dash, never as the raw key, and the translation hooks re-render once the periods arrive.
    interpolation: { escapeValue: false, defaultVariables: { period: {} } },
    missingInterpolationHandler: (_text: string, match: RegExpExecArray) => (match[1]?.trim().startsWith(PERIOD_PREFIX) ? "—" : match[0]),
    react: { bindI18n: `languageChanged ${PERIODS_LOADED}` },
    returnNull: false,
  });
  // "{{count, number}}" groups a large figure as formatNumber does (12,857), with Western digits on Arabic screens too.
  instance.services.formatter?.add("number", (value: unknown, lng: string | undefined, opts: Intl.NumberFormatOptions) => formatNumber(Number(value), lng?.startsWith("ar") ? "ar" : "en", opts));

  applyDocumentLocale(initial);
  instance.on("languageChanged", (lng) => {
    const l: Locale = lng.startsWith("ar") ? "ar" : "en";
    applyDocumentLocale(l);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, l);
  });

  return instance;
}

export const useTranslation = useI18nTranslation;

/** "ModeratelyConcentrated" → "Moderately Concentrated" for identifiers without a translation. */
export function humanize(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim();
}

export type EnumGroup = keyof typeof enCommon.enums;

/** Localised label for backend identifiers (proposal kinds, document categories, guardrail rules, roles…); falls back to a humanised identifier. */
export function useEnumLabel(): (group: EnumGroup, value: string | null | undefined) => string {
  const { t } = useI18nTranslation();
  return (group, value) => (value ? t(`enums.${group}.${value}`, { defaultValue: humanize(value) }) : "");
}

export function useLocale(): { locale: Locale; dir: "rtl" | "ltr"; setLocale: (l: Locale) => void; toggle: () => void } {
  const { i18n } = useI18nTranslation();
  const locale: Locale = i18n.language.startsWith("ar") ? "ar" : "en";
  return {
    locale,
    dir: dirFor(locale),
    setLocale: (l) => void i18n.changeLanguage(l),
    toggle: () => void i18n.changeLanguage(locale === "ar" ? "en" : "ar"),
  };
}

const ARABIC_INDIC = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

export function toArabicIndic(input: string): string {
  return input.replace(/\d/g, (d) => ARABIC_INDIC[Number(d)] ?? d);
}

// Date formatting (date-fns) lives in its own module, so the index an app loads first does not carry date-fns.
export { formatDate, formatDateTime, formatRelative } from "./dates";

export { enCommon, arCommon };
export type { ArabicCatalog } from "./catalog";
